import { useState } from 'react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import PhotoBoothInbox from '../../../../../components/common/PhotoBoothInbox';

/**
 * The customer photo-booth queue, as a second tab over a tool's settings column.
 *
 * Shops collect customer photos through the booth QR; a tool that takes a
 * picture should be able to pick one straight off that queue instead of asking
 * the shop to download it first. The tab strip only appears once the booth
 * answers that it is switched on for this office space, so for everybody else
 * the column looks exactly as it did.
 *
 * The queue stays mounted while the settings tab is showing — that is what lets
 * its on/off state resolve at all — so `children` is hidden rather than
 * unmounted, and neither side loses its state when the tabs are flipped.
 *
 * A tool that also works on the PDF files customers send passes `kinds={['image', 'file']}`, and `onPickFile(file, uploadId, meta)`
 * (instead of `onPick`) to hear which booth item a File came from and whether the customer marked it private. `initialTab="booth"`
 * opens on the queue (once the booth turns out to be on), `refreshKey` re-reads it, `onStatusChange(on)` reports whether it is on.
 * `footer` (optional) is shown under whichever tab is open — and when the booth is off — e.g. a link to the booth's settings.
 *
 * @param {{ onPick?: (file: File) => void, onPickFile?: (file: File, uploadId: number, meta: object) => void,
 *   kinds?: string[], initialTab?: 'settings' | 'booth', refreshKey?: number, onStatusChange?: (on: boolean) => void,
 *   footer?: import('react').ReactNode, children: import('react').ReactNode }} props
 */
export default function ToolBoothTabs({ onPick, onPickFile, kinds, initialTab = 'settings', refreshKey, onStatusChange, footer, children }) {
    const { t } = useTranslation();
    const [boothOn, setBoothOn] = useState(false);
    const [tab, setTab] = useState(initialTab);
    const showingBooth = boothOn && tab === 'booth';

    return (
        <>
            {boothOn && (
                <div className="grid grid-cols-2 gap-1.5 rounded-xl bg-zinc-100 p-1 dark:bg-zinc-800">
                    {[
                        { id: 'settings', label: t('toolPage.settingsTab') },
                        { id: 'booth', label: t('photoCollectorPanel.tabTitle') },
                    ].map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => setTab(item.id)}
                            className={`rounded-lg py-1.5 text-xs font-semibold transition-colors ${
                                tab === item.id
                                    ? 'bg-white text-primary shadow-sm dark:bg-zinc-900'
                                    : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
                            }`}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            )}

            <div className={showingBooth ? '' : 'hidden'}>
                <PhotoBoothInbox
                    onPickFile={(file, uploadId, meta) => {
                        if (onPickFile) onPickFile(file, uploadId, meta);
                        else onPick(file);
                        setTab('settings');
                    }}
                    onStatusChange={(on) => { setBoothOn(on); onStatusChange?.(on); }}
                    kinds={kinds}
                    refreshKey={refreshKey}
                />
            </div>

            <div className={showingBooth ? 'hidden' : 'space-y-3'}>
                {children}
            </div>

            {footer}
        </>
    );
}
